import useAnalytics from '@hooks/use-analytics'
import type { TypeProvider } from '@lib/types'
import { Typography, Modal, ExpandableContent, Collapsible } from '@shc/ui'
import { Fragment, useEffect, useId, useRef, type Dispatch, type SetStateAction } from 'react'

type ProviderInsuranceModalProps = {
  provider: TypeProvider
  isModalOpen: boolean
  setIsModalOpen: Dispatch<SetStateAction<boolean>>
}

const ProviderInsuranceModal = ({
  provider,
  isModalOpen,
  setIsModalOpen,
}: ProviderInsuranceModalProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const titleRef = useRef<HTMLElement>(null)
  const titleId = useId()
  const descriptionId = useId()

  const groupedPlans: Record<string, string[]> = {}
  for (const insurance of provider.insurances_facet) {
    const [providerName, plan] = insurance.split(' > ')
    if (providerName in groupedPlans) {
      if (plan) {
        groupedPlans[providerName].push(plan)
      }
    } else {
      groupedPlans[providerName] = plan ? [plan] : []
    }
  }

  const { track: trackSnowplow } = useAnalytics()

  useEffect(() => {
    if (!isModalOpen) return

    let frameId: number | null = null

    const focusTitle = () => {
      titleRef.current?.focus()
    }

    if (dialogRef.current?.open) {
      focusTitle()
    } else {
      frameId = globalThis.requestAnimationFrame(() => {
        if (dialogRef.current?.open) {
          focusTitle()
        }
      })
    }

    return () => {
      if (frameId !== null) {
        globalThis.cancelAnimationFrame(frameId)
      }
    }
  }, [isModalOpen])

  const handleOpenChange = (state: boolean, insurer_name: string) => {
    trackSnowplow({
      event: { name: state ? 'component_expand' : 'component_collapse', data: {} },
      contexts: [
        {
          name: 'component',
          data: {
            component_text: insurer_name,
          },
        },
      ],
    })
  }

  return (
    <Modal
      ref={dialogRef}
      width="md"
      open={isModalOpen}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onClose={() => {
        setIsModalOpen(false)
        trackSnowplow({
          event: { name: 'modal_close', data: {} },
          contexts: [{ name: 'modal', data: { modal_name: 'InsuranceModal' } }],
        })
      }}>
      <div className="flex flex-col" data-testid="insurance-modal">
        <Typography
          as="h2"
          id={titleId}
          ref={titleRef}
          tabIndex={-1}
          variant="h2"
          align="center"
          className="px-7 pb-8 focus:outline-none">
          Insurance plans accepted
        </Typography>
        <Typography id={descriptionId} className="pb-8">
          Sharp accepts most health insurance. Not all plans are listed. Please contact your
          insurance company to verify your insurance type and plan.
        </Typography>
        <Typography variant="body-semibold" className="pb-4">
          In-network insurance plans
        </Typography>
        <ExpandableContent>
          { Object.keys(groupedPlans).map((k: string) => (
            <Collapsible
              key={k}
              heading={k}
              description={
                <div>
                  {groupedPlans[k].map((plan: string) => (
                    <Fragment key={plan}>
                      <Typography>{plan}</Typography>
                    </Fragment>
                  ))}
                </div>
              }
              onOpenChange={(open: boolean) => handleOpenChange(open, k)}
            />
          ))}
        </ExpandableContent>
      </div>
    </Modal>
  )
}

export default ProviderInsuranceModal
